<template>
  <section v-if="shouldRender" id="trending-stories" class="tsr" aria-label="Trending story clusters">
    <div class="tsr__header">
      <div>
        <span class="tsr__eyebrow">
          <i class="fa fa-layer-group" aria-hidden="true"></i>
          BarbershopTalk Intelligence
        </span>
        <h2>{{ title }}</h2>
        <p>{{ subtitle }}</p>
      </div>
      <span v-if="!loading && displayClusters.length" class="tsr__live-pill">
        <span class="tsr__live-dot" aria-hidden="true"></span>
        Live story momentum
      </span>
    </div>

    <div v-if="loading" class="tsr__rail" aria-live="polite" aria-label="Loading story clusters">
      <article v-for="n in 3" :key="n" class="tsr__card tsr__card--skeleton">
        <div class="tsr__skeleton tsr__skeleton--image"></div>
        <div class="tsr__skeleton-body">
          <span class="tsr__skeleton tsr__skeleton--short"></span>
          <span class="tsr__skeleton tsr__skeleton--long"></span>
          <span class="tsr__skeleton tsr__skeleton--mid"></span>
        </div>
      </article>
    </div>

    <div v-else class="tsr__rail">
      <article
        v-for="cluster in displayClusters"
        :key="cluster.clusterId"
        class="tsr__card"
        :class="{ 'tsr__card--political': isPolitical(cluster) }"
      >
        <div class="tsr__media">
          <img
            v-if="cluster.primaryArticle.imageUrl"
            class="tsr__image"
            v-lazy="clusterImage(cluster)"
            :alt="cluster.primaryArticle.title || 'Story image'"
          >
          <div v-else class="tsr__image tsr__image--empty" aria-hidden="true">
            <i class="fa fa-newspaper"></i>
          </div>
          <span class="tsr__media-badge">
            {{ reportLabel(cluster) }}
          </span>
        </div>

        <div class="tsr__body">
          <div class="tsr__meta">
            <span>{{ cluster.primaryArticle.sourceName || 'Tracked report' }}</span>
            <span v-if="formatPublished(cluster.lastUpdatedAt || cluster.primaryArticle.publishedAt)">
              {{ formatPublished(cluster.lastUpdatedAt || cluster.primaryArticle.publishedAt) }}
            </span>
          </div>

          <a
            class="tsr__title-link"
            :href="cluster.primaryArticle.urlPath || '#'"
            @click.prevent="navigateToArticle(cluster)"
          >
            <h3>{{ cluster.primaryArticle.title || 'Story gaining coverage' }}</h3>
          </a>

          <p class="tsr__summary">
            {{ storySummary(cluster) }}
          </p>

          <div
            v-if="isPolitical(cluster) && cluster.biasBreakdown"
            class="tsr__bias"
            :aria-label="sourceMixAria(cluster)"
            :title="sourceMixTooltip"
            tabindex="0"
          >
            <div class="tsr__bias-track" aria-hidden="true">
              <span class="tsr__bias-segment tsr__bias-segment--left" :style="{ width: biasPercent(cluster, 'left') + '%' }"></span>
              <span class="tsr__bias-segment tsr__bias-segment--center" :style="{ width: biasPercent(cluster, 'center') + '%' }"></span>
              <span class="tsr__bias-segment tsr__bias-segment--right" :style="{ width: biasPercent(cluster, 'right') + '%' }"></span>
            </div>
            <div class="tsr__bias-labels">
              <span>L {{ biasCount(cluster, 'left') }}</span>
              <span>C {{ biasCount(cluster, 'center') }}</span>
              <span>R {{ biasCount(cluster, 'right') }}</span>
            </div>
          </div>

          <div class="tsr__chips">
            <span class="tsr__chip">
              <i class="fa fa-bolt" aria-hidden="true"></i>
              {{ isPolitical(cluster) ? 'Perspective spread' : 'Coverage momentum' }}
            </span>
            <span v-if="isPolitical(cluster) && cluster.emotionalGap !== null" class="tsr__chip tsr__chip--gap">
              {{ Math.round(Number(cluster.emotionalGap)) }} gap
            </span>
          </div>

          <button
            class="tsr__cta"
            type="button"
            :aria-label="`Compare coverage for ${cluster.primaryArticle.title || 'this story'}`"
            @click="openCluster(cluster)"
          >
            Compare coverage
            <i class="fa fa-arrow-right" aria-hidden="true"></i>
          </button>
        </div>
      </article>
    </div>

    <CompareCoverageModal
      v-if="selectedCluster"
      :cluster-id="selectedCluster.clusterId"
      :article-title="selectedCluster.primaryArticle?.title || ''"
      @close="selectedCluster = null"
    />
  </section>
</template>

<script>
import ArticleDataService from '@/services/ArticleDataService'
import CompareCoverageModal from '@/components/CompareCoverageModal.vue'
import { optimizeImage } from '@/utils/imageOptimize'
import { SOURCE_MIX_TOOLTIP } from '@/utils/framingHelpers'

export default {
  name: 'TrendingStoriesRail',

  components: {
    CompareCoverageModal,
  },

  props: {
    category: {
      type: String,
      default: null,
    },
    hours: {
      type: Number,
      default: 24,
    },
    limit: {
      type: Number,
      default: 10,
    },
    title: {
      type: String,
      default: 'Stories Everyone Is Covering',
    },
    subtitle: {
      type: String,
      default: 'Track the stories multiple reports are chasing right now.',
    },
  },

  data() {
    return {
      loading: true,
      clusters: [],
      selectedCluster: null,
      sourceMixTooltip: SOURCE_MIX_TOOLTIP,
    }
  },

  computed: {
    displayClusters() {
      return this.clusters.filter(cluster => cluster?.clusterId && cluster?.primaryArticle)
    },

    shouldRender() {
      return this.loading || this.displayClusters.length > 0
    },
  },

  mounted() {
    this.fetchClusters()
  },

  watch: {
    category() {
      this.fetchClusters()
    },
    hours() {
      this.fetchClusters()
    },
    limit() {
      this.fetchClusters()
    },
  },

  methods: {
    async fetchClusters() {
      this.loading = true
      try {
        const response = await ArticleDataService.getTrendingStoryClusters({
          category: this.category,
          hours: this.hours,
          limit: this.limit,
        })
        const payload = response?.data
        this.clusters = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.clusters)
            ? payload.clusters
            : []
      } catch (error) {
        console.error('Failed to load story clusters:', error)
        this.clusters = []
      } finally {
        this.loading = false
      }
    },

    openCluster(cluster) {
      this.selectedCluster = cluster
    },

    isPolitical(cluster) {
      return cluster?.biasMode === 'political'
    },

    reportCount(cluster) {
      const count = Number(cluster?.articleCount)
      return Number.isFinite(count) && count > 0 ? Math.round(count) : 0
    },

    reportLabel(cluster) {
      const count = this.reportCount(cluster)
      if (!count) return this.isPolitical(cluster) ? 'Perspective spread' : 'Reports tracking'
      const noun = count === 1 ? 'report' : 'reports'
      return this.isPolitical(cluster)
        ? `${count} ${noun} across perspectives`
        : `${count} ${noun} tracking this`
    },

    storySummary(cluster) {
      if (this.isPolitical(cluster)) {
        if (cluster.emotionalGap !== null && cluster.emotionalGap !== undefined) {
          return `${Math.round(Number(cluster.emotionalGap))}-point emotional gap detected across political coverage.`
        }
        return 'Compare how political outlets are framing the same developing story.'
      }
      return 'Compare how multiple reports are framing the same story as it gains momentum.'
    },

    clusterImage(cluster) {
      return optimizeImage(cluster?.primaryArticle?.imageUrl, 420, 236)
    },

    biasCount(cluster, key) {
      const count = Number(cluster?.biasBreakdown?.[key])
      return Number.isFinite(count) && count > 0 ? Math.round(count) : 0
    },

    sourceMixAria(cluster) {
      const left = this.biasCount(cluster, 'left')
      const center = this.biasCount(cluster, 'center')
      const right = this.biasCount(cluster, 'right')
      return `Source mix: ${left} left, ${center} center, ${right} right sources. ${SOURCE_MIX_TOOLTIP}`
    },

    biasPercent(cluster, key) {
      const left = this.biasCount(cluster, 'left')
      const center = this.biasCount(cluster, 'center')
      const right = this.biasCount(cluster, 'right')
      const total = left + center + right
      if (!total) return 0
      return Math.round((this.biasCount(cluster, key) / total) * 100)
    },

    navigateToArticle(cluster) {
      const path = cluster?.primaryArticle?.urlPath
      if (path) this.$router.push(path)
    },

    formatPublished(value) {
      if (!value) return ''

      const publishedAt = new Date(value).getTime()
      if (!Number.isFinite(publishedAt)) return ''

      const diffMinutes = Math.floor((Date.now() - publishedAt) / 60000)
      if (diffMinutes < 1) return 'just now'
      if (diffMinutes < 60) return `${diffMinutes}m ago`

      const diffHours = Math.floor(diffMinutes / 60)
      if (diffHours < 24) return `${diffHours}h ago`

      const diffDays = Math.floor(diffHours / 24)
      return `${diffDays}d ago`
    },
  },
}
</script>

<style scoped>
.tsr {
  overflow: hidden;
  margin: 14px 0 22px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 26px;
  background:
    radial-gradient(circle at top left, rgba(37, 99, 235, 0.14), transparent 32%),
    linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.94));
  box-shadow: 0 18px 55px rgba(15, 23, 42, 0.12);
}

.tsr__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  padding: 20px 22px 4px;
}

.tsr__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #2563eb;
  font-size: 0.72rem;
  font-weight: 950;
  letter-spacing: 0.1em;
  line-height: 1;
  text-transform: uppercase;
}

.tsr__header h2 {
  margin: 8px 0 4px;
  color: #0f172a;
  font-size: clamp(1.25rem, 2.4vw, 1.8rem);
  font-weight: 950;
  letter-spacing: -0.04em;
}

.tsr__header p {
  max-width: 680px;
  margin: 0;
  color: #64748b;
  font-size: 0.94rem;
  font-weight: 700;
  line-height: 1.45;
}

.tsr__live-pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex: 0 0 auto;
  margin-top: 4px;
  padding: 8px 11px;
  border: 1px solid rgba(37, 99, 235, 0.18);
  border-radius: 999px;
  background: rgba(239, 246, 255, 0.86);
  color: #1d4ed8;
  font-size: 0.74rem;
  font-weight: 900;
}

.tsr__live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 5px rgba(34, 197, 94, 0.14);
}

.tsr__rail {
  display: grid;
  grid-auto-columns: minmax(280px, 360px);
  grid-auto-flow: column;
  gap: 16px;
  overflow-x: auto;
  padding: 18px 22px 22px;
  scroll-snap-type: x proximity;
  scrollbar-width: thin;
}

.tsr__card {
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 24px;
  background: #ffffff;
  box-shadow: 0 16px 38px rgba(15, 23, 42, 0.12);
  scroll-snap-align: start;
  transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease;
}

.tsr__card:hover {
  border-color: rgba(37, 99, 235, 0.28);
  box-shadow: 0 22px 52px rgba(15, 23, 42, 0.18);
  transform: translateY(-3px);
}

.tsr__card--political {
  border-color: rgba(59, 130, 246, 0.24);
}

.tsr__media {
  position: relative;
  overflow: hidden;
  background: #0f172a;
}

.tsr__image {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
}

.tsr__image--empty {
  display: grid;
  place-items: center;
  color: rgba(255, 255, 255, 0.72);
  font-size: 2rem;
}

.tsr__media::after {
  position: absolute;
  inset: auto 0 0;
  height: 50%;
  background: linear-gradient(180deg, transparent, rgba(15, 23, 42, 0.78));
  content: '';
  pointer-events: none;
}

.tsr__media-badge {
  position: absolute;
  right: 11px;
  bottom: 11px;
  z-index: 1;
  max-width: calc(100% - 22px);
  padding: 7px 10px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.82);
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 950;
  line-height: 1;
  box-shadow: 0 10px 24px rgba(2, 6, 23, 0.25);
  backdrop-filter: blur(10px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tsr__body {
  display: flex;
  flex-direction: column;
  gap: 11px;
  padding: 16px;
}

.tsr__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 850;
  text-transform: uppercase;
}

.tsr__title-link {
  display: block;
  color: inherit;
  text-decoration: none;
}

.tsr__title-link:hover h3 {
  color: #2563eb;
}

.tsr__title-link:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 4px;
  border-radius: 6px;
}

.tsr__body h3 {
  display: -webkit-box;
  min-height: 3.4em;
  margin: 0;
  overflow: hidden;
  color: #0f172a;
  font-size: 1rem;
  font-weight: 950;
  letter-spacing: -0.02em;
  line-height: 1.14;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.tsr__summary {
  min-height: 2.8em;
  margin: 0;
  color: #475569;
  font-size: 0.84rem;
  font-weight: 700;
  line-height: 1.42;
}

.tsr__bias {
  display: grid;
  gap: 7px;
  outline: none;
  border-radius: 8px;
}

.tsr__bias:focus {
  box-shadow: 0 0 0 2px rgba(23, 138, 239, 0.35);
}

.tsr__bias-track {
  display: flex;
  overflow: hidden;
  height: 8px;
  border-radius: 999px;
  background: #e2e8f0;
}

.tsr__bias-segment {
  min-width: 0;
}

.tsr__bias-segment--left {
  background: #2563eb;
}

.tsr__bias-segment--center {
  background: #94a3b8;
}

.tsr__bias-segment--right {
  background: #dc2626;
}

.tsr__bias-labels {
  display: flex;
  justify-content: space-between;
  color: #64748b;
  font-size: 0.68rem;
  font-weight: 900;
}

.tsr__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.tsr__chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 8px;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 0.7rem;
  font-weight: 950;
}

.tsr__chip i {
  margin: 0;
  color: inherit;
}

.tsr__chip--gap {
  background: #fef2f2;
  color: #b91c1c;
}

.tsr__cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  margin-top: auto;
  padding: 10px 12px;
  border: 0;
  border-radius: 14px;
  background: linear-gradient(135deg, #0f172a, #1e3a8a);
  color: #ffffff;
  cursor: pointer;
  font-size: 0.86rem;
  font-weight: 950;
  transition: transform 160ms ease, box-shadow 160ms ease;
}

.tsr__cta:hover {
  box-shadow: 0 12px 24px rgba(30, 58, 138, 0.24);
  transform: translateY(-1px);
}

.tsr__cta i {
  margin: 0;
  color: inherit;
}

.tsr__card--skeleton {
  padding: 0;
  pointer-events: none;
}

.tsr__skeleton {
  display: block;
  border-radius: 999px;
  background: linear-gradient(90deg, #e2e8f0, #f8fafc, #e2e8f0);
  background-size: 200% 100%;
  animation: tsr-shimmer 1.2s infinite linear;
}

.tsr__skeleton--image {
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 0;
}

.tsr__skeleton-body {
  display: grid;
  gap: 12px;
  padding: 16px;
}

.tsr__skeleton--short {
  width: 36%;
  height: 12px;
}

.tsr__skeleton--long {
  width: 92%;
  height: 16px;
}

.tsr__skeleton--mid {
  width: 66%;
  height: 14px;
}

@keyframes tsr-shimmer {
  from {
    background-position: 200% 0;
  }
  to {
    background-position: -200% 0;
  }
}

@media (max-width: 780px) {
  .tsr {
    margin: 12px 0 18px;
    border-radius: 20px;
  }

  .tsr__header {
    flex-direction: column;
    padding: 16px 16px 0;
  }

  .tsr__live-pill {
    margin-top: 0;
  }

  .tsr__rail {
    grid-auto-columns: minmax(250px, 82vw);
    gap: 12px;
    padding: 16px;
  }
}
</style>
